import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import isNotEmptyReactElementContent from 'core/utils/content';

import Spinner from '../Icon/Spinner';

import styles from './index.styl';

const marginMapping = {
  childless: 'margin',
  top: 'marginBottom',
  right: 'marginLeft',
  bottom: 'marginTop',
  left: 'marginRight',
};


/**
 * @author Pavel Povalyaev <p.povalyaev@rambler-co.ru>
 */
export default function Loader(props) {
  const {
    children,
    spinnerMargin,
    spinnerPosition,
    spinnerWidth,
    spinnerHeight,
    spinnerAsText,
    loaderPosition,
  } = props;

  const hasChildren = isNotEmptyReactElementContent(children);
  const marginKey = hasChildren ? spinnerPosition : 'childless';

  return (
    <div className={cx(styles.loader, spinnerPosition && styles['_' + spinnerPosition], loaderPosition && styles[`loaderPosition_${loaderPosition}`])}>
      {spinnerAsText
        ? (
          <div className={styles.spinnerAsText}>
            {typeof spinnerAsText === 'string'
              ? spinnerAsText
              : 'Загрузка'} <span>.</span><span>.</span><span>.</span>
          </div>
        )
        : (
          <Spinner
            width={spinnerWidth}
            height={spinnerHeight}
            {...spinnerMargin && { style: { [marginMapping[marginKey]]: spinnerMargin } }}
          />
        )
      }
      {hasChildren &&
        <div>
          {children}
        </div>
      }
    </div>
  );
}

Loader.propTypes = {
  /**
   * Устанавливает отступ между спиннером и элементами, переданными внутрь
   * `Loader`. Если children нет, то это значение передаётся элементу спиннера as is.
   *
   * Должно быть валидным значением CSS-свойства margin.
   */
  spinnerMargin: PropTypes.string,

  /**
   * Задаёт расположение спиннера относительно переданного внутрь Loader'а
   * содержимого
   */
  spinnerPosition: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),

  /**
   * Задаёт расположение loader
   */
  loaderPosition: PropTypes.oneOf(['right', 'center', 'left']),

  /**
   * Ширина спиннера
   */
  spinnerWidth: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),

  /**
   * Высота спиннера
   */
  spinnerHeight: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),

  /**
   * Заменяет отображение спиннера иконки, на простой текст.
   * Текстовое отображение наследует стили текста, от родителя,
   * что позволяет избавиться от скачка лейатуа, после того как компонент загрузился.
   */
  spinnerAsText: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
};

Loader.defaultProps = {
  spinnerPosition: 'left',
  spinnerWidth: 32,
  spinnerHeight: 32,
  loaderPosition: 'center',
};

/**
 * @author Anton Ignatov <a.ignatov@rambler-co.ru>
 *
 * Простой лоадер который ничего не отображает кроме спинера.
 * Может быть передан, как компонент в HOC `loader`.
 */
export function SimpleLoader(props) {
  return (
    <Loader {...props} />
  );
}

export { default as SkeletonLoader } from './SkeletonLoader';
